/**
 * Alert rules — list + create/edit dialog.
 *
 * Each rule = "when (condition) on (target), notify with (severity)
 * via (channels), then sleep (cooldown)". The DBOS-scheduled
 * evaluator on the backend wakes every minute and fires breached
 * rules that are outside cooldown.
 *
 * The page intentionally surfaces the *current observed value* next
 * to each rule's threshold so the operator can see whether they're
 * close to breaching even when nothing has fired yet.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Pencil, Plus, TestTube2, Trash2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { AlertRuleDialog } from "@/components/alerts/AlertRuleDialog";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { DescriptionTooltip } from "@/components/ui/description-tooltip";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Switch } from "@/components/ui/switch";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { alertsApi, type AlertRule } from "@/lib/api-alerts";
import { cn } from "@/lib/cn";
import { usePermissions } from "@/lib/permissions";


// ---------------------------------------------------------------------------
// Per-type formatters (kept here to avoid leaking them into the API client)
// ---------------------------------------------------------------------------

function formatThreshold(r: AlertRule): string {
  const cfg = r.condition_config as Record<string, unknown>;
  if (r.condition_type === "failure_count") {
    return `> ${cfg.threshold ?? "?"} failures`;
  }
  if (r.condition_type === "latency_p95") {
    return `p95 > ${cfg.threshold_ms ?? "?"}ms`;
  }
  if (r.condition_type === "absence") {
    return `no run in ${r.window_minutes}min`;
  }
  return "—";
}

function formatTarget(r: AlertRule): string {
  if (r.target_kind === "all") return "any workflow";
  if (r.target_kind === "workflow") return `workflow ${r.target_workflow_id?.slice(0, 8) ?? ""}…`;
  if (r.target_kind === "direction") return `direction: ${r.target_direction}`;
  if (r.target_kind === "tag") return `tag: ${r.target_tag}`;
  return r.target_kind;
}

function formatObserved(r: AlertRule): string {
  if (r.last_observed_value === null) return "—";
  if (r.condition_type === "latency_p95") {
    return `${Math.round(r.last_observed_value)}ms`;
  }
  if (r.condition_type === "absence") {
    const min = Math.round(r.last_observed_value / 60);
    return min > 100_000 ? "never ran" : `${min}min ago`;
  }
  return String(Math.round(r.last_observed_value));
}

const SEVERITY_TONE: Record<AlertRule["severity"], "warning" | "danger"> = {
  warning: "warning",
  error: "danger",
  critical: "danger",
};


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AlertsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("alerts", "write");
  const canDelete = can("alerts", "delete");

  const [dialog, setDialog] = React.useState<{ open: boolean; edit: AlertRule | null }>({
    open: false,
    edit: null,
  });

  const rulesQ = useQuery({
    queryKey: ["alerts"],
    queryFn: () => alertsApi.list(),
    refetchInterval: 30_000,
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      enabled ? alertsApi.enable(id) : alertsApi.disable(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });

  const remove = useMutation({
    mutationFn: alertsApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });

  const test = useMutation({
    mutationFn: alertsApi.test,
  });

  const rules = rulesQ.data ?? [];

  return (
    <PageContainer>
      <PageHeader
        title={t("alerts.title", "Alert rules")}
        description={t(
          "alerts.subtitle",
          "Evaluated every minute. On breach (and outside cooldown), a notification fires with the configured severity.",
        )}
        actions={
          canWrite && (
            <Button
              size="sm"
              onClick={() => setDialog({ open: true, edit: null })}
            >
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("alerts.new", "New rule")}
            </Button>
          )
        }
      />
      <PageContent>
        {rules.length === 0 && !rulesQ.isLoading ? (
          <EmptyState
            icon={Bell}
            title={t("alerts.empty", "No alert rules yet")}
            description={t(
              "alerts.emptyDescription",
              "Define a rule to be notified when a workflow misbehaves: too many failures, slow latency, or no traffic at all.",
            )}
            action={
              canWrite && (
                <Button onClick={() => setDialog({ open: true, edit: null })}>
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  {t("alerts.new", "New rule")}
                </Button>
              )
            }
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-12">On</Th>
                  <Th>Name</Th>
                  <Th>Target</Th>
                  <Th>Condition</Th>
                  <Th className="w-32 text-right">Observed</Th>
                  <Th className="w-24">Severity</Th>
                  <Th>Last fired</Th>
                  {(canWrite || canDelete) && (
                    <Th className="w-40 text-right">Actions</Th>
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {rules.map((r) => (
                  <Tr key={r.id}>
                    <Td onClick={(e) => e.stopPropagation()}>
                      {canWrite ? (
                        <Switch
                          checked={r.enabled}
                          onCheckedChange={(v) =>
                            toggle.mutate({ id: r.id, enabled: v })
                          }
                        />
                      ) : (
                        <Badge tone={r.enabled ? "active" : "neutral"}>
                          {r.enabled ? "on" : "off"}
                        </Badge>
                      )}
                    </Td>
                    <Td>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium">{r.name}</span>
                        <DescriptionTooltip text={r.description} />
                      </div>
                    </Td>
                    <Td className="font-mono text-xs">{formatTarget(r)}</Td>
                    <Td className="font-mono text-xs">{formatThreshold(r)}</Td>
                    <Td className="text-right font-mono text-xs">
                      <span
                        className={cn(
                          r.last_observed_value !== null &&
                            r.last_observed_value > (
                              (r.condition_config as Record<string, number>).threshold ??
                                (r.condition_config as Record<string, number>).threshold_ms ??
                                r.window_minutes * 60
                            )
                            ? "text-danger"
                            : "text-muted-foreground",
                        )}
                      >
                        {formatObserved(r)}
                      </span>
                    </Td>
                    <Td>
                      <Badge tone={SEVERITY_TONE[r.severity]}>{r.severity}</Badge>
                    </Td>
                    <Td className="text-xs text-muted-foreground">
                      {r.last_fired_at ? new Date(r.last_fired_at).toLocaleString() : "—"}
                    </Td>
                    {(canWrite || canDelete) && (
                      <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {canWrite && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Test"
                              title="Evaluate now (dry-run)"
                              onClick={() => test.mutate(r.id)}
                            >
                              <TestTube2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </Button>
                          )}
                          {canWrite && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Edit"
                              onClick={() => setDialog({ open: true, edit: r })}
                            >
                              <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Delete"
                              onClick={() => {
                                if (confirm(`Delete alert rule "${r.name}"?`)) {
                                  remove.mutate(r.id);
                                }
                              }}
                            >
                              <Trash2
                                className="h-3.5 w-3.5 text-danger"
                                strokeWidth={1.5}
                              />
                            </Button>
                          )}
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}

        {/* Surface the result of the most-recent "test" click. Brief
            but informative; the user clicks Test to sanity-check
            thresholds without waiting for the scheduler tick. */}
        {test.data && (
          <Alert
            tone={test.data.would_fire ? "danger" : "info"}
            title={test.data.would_fire ? "Would fire" : "Would NOT fire"}
            className="mt-4"
          >
            <div className="font-mono text-xs">
              observed = {test.data.observed_value} · threshold = {test.data.threshold}
              {test.data.in_cooldown && " · in cooldown (suppressed)"}
            </div>
          </Alert>
        )}
      </PageContent>

      <AlertRuleDialog
        open={dialog.open}
        edit={dialog.edit}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
      />
    </PageContainer>
  );
}
